/**
 *  @class
 *  @function VideoCarousel
 */
if (!customElements.get('video-carousel')) {
	class VideoCarousel extends HTMLElement {
		constructor() {
			super();
			this.track = this.querySelector('.video-carousel__track');
			this.slides = [];
			this.isAnimating = false;
			this.durationMs = 450;
			this.currentIndex = 0;
			this.touchStartX = 0;
			this.touchEndX = 0;
			this.minSwipeDistance = 50;
		}

		connectedCallback() {
			this.updateSlides();
			if (!this.track || this.slides.length === 0) return;
			this.init();
		}

		updateSlides() {
			this.slides = Array.from(this.querySelectorAll('.video-carousel__slide:not(.clone)'));
		}

		init() {
			this.createClones();
			const allSlides = Array.from(this.querySelectorAll('.video-carousel__slide'));

			const setActive = () => {
				const activeIndex = Math.round(this.currentIndex);

				allSlides.forEach((slide, idx) => {
					// The active slide could be one of the originals or one of the clones.
					const originalIndex = idx % this.slides.length;
					const currentOriginalIndex = activeIndex % this.slides.length;

					if (originalIndex === currentOriginalIndex) {
						slide.classList.add('active');
					} else {
						slide.classList.remove('active');
						slide.classList.remove('playing');
						const video = slide.querySelector('video');
						if (video) video.pause();
					}
				});
			};

			const slideTo = (index) => {
				if (!this.track) return;
				this.isAnimating = true;
				const slideWidth = this.slides[0].offsetWidth;
				const gap = parseInt(getComputedStyle(this.track).gap, 10) || 0;
				const baseOffset = (this.offsetWidth - slideWidth) / 2;
				let newX = baseOffset - index * (slideWidth + gap);

				this.track.style.transition = `transform ${this.durationMs}ms ease`;
				this.track.style.transform = `translateX(${newX}px)`;
				this.currentIndex = index;

				setActive();

				setTimeout(() => {
					this.isAnimating = false;

					const originalSlidesCount = this.slides.length;
					let needsReset = false;

					if (this.currentIndex <= originalSlidesCount - 1) {
						this.currentIndex += originalSlidesCount;
						needsReset = true;
					} else if (this.currentIndex >= originalSlidesCount * 2) {
						this.currentIndex -= originalSlidesCount;
						needsReset = true;
					}

					if (needsReset) {
						newX = baseOffset - this.currentIndex * (slideWidth + gap);
						this.track.style.transition = 'none';
						this.track.style.transform = `translateX(${newX}px)`;
					}
				}, this.durationMs);
			};

			this.slideTo = slideTo;

			this.addEventListener('click', (e) => {
				if (this.isAnimating) return;
				const slide = e.target.closest('.video-carousel__slide');
				if (!slide) return;

				const clickedIndex = allSlides.indexOf(slide);

				if (clickedIndex === this.currentIndex) {
					this.togglePlayback(slide);
					return;
				}

				slideTo(clickedIndex);
			});

			this.addEventListener('touchstart', (e) => {
				this.touchStartX = e.changedTouches[0].screenX;
			}, { passive: true });

			this.addEventListener('touchend', (e) => {
				if (this.isAnimating) return;
				this.touchEndX = e.changedTouches[0].screenX;
				this.handleSwipe(slideTo);
			}, { passive: true });

			// Initial setup
			this.currentIndex = this.slides.length;
			const slideWidth = this.slides[0].offsetWidth;
			const gap = parseInt(getComputedStyle(this.track).gap, 10) || 0;
			const baseOffset = (this.offsetWidth - slideWidth) / 2;
			const initialX = baseOffset - this.currentIndex * (slideWidth + gap);
			this.track.style.transition = 'none';
			this.track.style.transform = `translateX(${initialX}px)`;
			setTimeout(setActive, 50);
		}

		createClones() {
			if (!this.track || this.slides.length === 0) return;
			const slidesToPrepend = this.slides.map((slide) => {
				const clone = slide.cloneNode(true);
				clone.classList.add('clone');
				return clone;
			});
			const slidesToAppend = this.slides.map((slide) => {
				const clone = slide.cloneNode(true);
				clone.classList.add('clone');
				return clone;
			});
			this.track.prepend(...slidesToPrepend);
			this.track.append(...slidesToAppend);
		}

		togglePlayback(slide) {
			const video = slide.querySelector('video');
			if (!video) return;
			if (video.paused) {
				this.pauseAll(video);
				video.play();
				slide.classList.add('playing');
			} else {
				video.pause();
				slide.classList.remove('playing');
			}
		}

		pauseAll(exceptVideo) {
			this.querySelectorAll('.video-carousel__slide').forEach((slide) => {
				slide.classList.remove('playing');
				const video = slide.querySelector('video');
				if (video && video !== exceptVideo) video.pause();
			});
		}

		handleSwipe(slideTo) {
			const swipeDistance = this.touchEndX - this.touchStartX;

			if (Math.abs(swipeDistance) < this.minSwipeDistance) return;

			if (swipeDistance > 0) {
				// Swipe right - go to previous slide
				slideTo(this.currentIndex - 1);
			} else {
				// Swipe left - go to next slide
				slideTo(this.currentIndex + 1);
			}
		}
	}
	customElements.define('video-carousel', VideoCarousel);
}
